import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { TranslocoPipe } from '@jsverse/transloco';
import { produce } from 'immer';
import { GuidelineService } from 'shared/domain';
import {
  ButtonDirective,
  ConfirmationDialogItem,
  ConfirmationDialogNewComponent,
  DialogService,
  FormFieldImports,
  SelectImports,
  SelectOptionComponent
} from 'shared/ui-common';
import { isTruthy } from 'shared/util-common';

import {
  ComponentExpandStrategy,
  ComponentListType,
  ComponentListWidget,
  GuidelineChapterType,
  GuidelineComponentType,
} from '@treetop/shared/model';

@Component({
  standalone: true,
  imports: [SelectOptionComponent, TranslocoPipe, SelectImports, FormFieldImports, MatTab, MatTabGroup, ButtonDirective],
  selector: 'guideline-editor-component-list-sidebar-wrapper',
  templateUrl: './guideline-editor-component-list-sidebar-wrapper.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./guideline-editor-component-list-sidebar-wrapper.component.scss'],
})
export class GuidelineEditorComponentListSidebarWrapperComponent {
  private guidelineService = inject(GuidelineService);
  private dialogService = inject(DialogService);

  protected widget = input.required<ComponentListWidget>();

  protected setExpandComponentsOnRead(expand: ComponentExpandStrategy) {
    const newWidget = produce(this.widget(), (draft) => {
      draft.componentExpandStrategyOnRead = expand;
    });

    this.guidelineService.updateWidget(newWidget.uniqueId, newWidget);
  }

  protected resetComponentListType() {
    this.dialogService
      .open<ConfirmationDialogItem>(ConfirmationDialogNewComponent, {
        width: 400,
        title: 'GuidelineEditor.Sidebar.Widgets.GuidelineEditor.ResetConfirmationDialog.Title',
        data: {
          text: 'GuidelineEditor.Sidebar.Widgets.GuidelineEditor.ResetConfirmationDialog.Message',
          confirmButton: 'GuidelineEditor.Sidebar.Widgets.GuidelineEditor.ResetConfirmationDialog.ConfirmButton',
        },
      })
      .afterClosed()
      .pipe(isTruthy())
      .subscribe(() => {
        const widget = this.widget();
        (widget.widgetIds ?? []).forEach((id) => this.guidelineService.removeWidget(id));

        const newWidget = produce(widget, (draft) => {
          draft.componentListType = undefined;
          draft.widgetIds = [];
        });
        this.guidelineService.updateWidget(newWidget.uniqueId, newWidget);
      });
  }

  protected GuidelineComponentType = GuidelineComponentType;
  protected readonly ComponentExpandStrategy = ComponentExpandStrategy;
  protected readonly GuidelineChapterType = GuidelineChapterType;
  protected readonly ComponentListType = ComponentListType;
}
